// React
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Icons
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";

// Components
import Button from '../../../../../../components/CustomButton';
import TextInput from '../../../../../../components/TextInput';
import ViewChangePasswordSuccess from './Success';

// Context
import { useDataSettingsContext } from '../../context';

// Styles
import styles from './style.module.scss';
import viewsStyles from '../views.module.scss';

// Utils
import { userChangePasswordUrl } from '../../../../../../utils/urls'
import useCsrfToken from '../../../../../../utils/hooks/csfrToken';

interface ViewChangePasswordProps {
  isCreate: boolean;
}

const ViewChangePassword: React.FC<ViewChangePasswordProps> = ({ isCreate }) => {
  const { t } = useTranslation();

  // CSRF Token
  const csrfToken = useCsrfToken();

  // Context
  const { onClose, onSetHeaderSubtitle, onSetHeaderTitle } = useDataSettingsContext();

  // =========================== States ===========================
  const [loading, setLoading] = useState<boolean>(false);

  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const [currentPassword, setCurrentPassword] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);

  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  // Effects
  useEffect(() => {
    onSetHeaderTitle(t('profile.settings.data.change_password.title'));
    onSetHeaderSubtitle(t('profile.settings.data.change_password.description'));

    return () => {
      onSetHeaderSubtitle('');
      onSetHeaderTitle('');
    };
  }, [onSetHeaderSubtitle, onSetHeaderTitle, t]);

  const clearStates = useCallback(() => {
    setCurrentPassword(null);
    setCurrentPasswordError(null);
    setNewPassword(null);
    setNewPasswordError(null);
    setConfirmPassword(null);
  }, []);

  const handleChangeNewPassword = useCallback((password: string) => {
    setNewPassword(password);
    if (password.length < 8) setNewPasswordError(t('profile.settings.data.change_password.new_password_helper'));
    else setNewPasswordError(null);
  }, []);
  const handleChangeConfirmPassword = useCallback((password: string) => {
    setConfirmPassword(password);
    setConfirmPasswordError(null);
  }, []);
  const handleChangeCurrentPassword = useCallback((password: string) => {
    setCurrentPassword(password);
    setCurrentPasswordError(null);
  }, []);

  const handleSavePassword = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);
    if (!isCreate && !currentPassword) {
      setCurrentPasswordError(t('profile.settings.data.change_password.current_password_required'));
      setLoading(false);
      return;
    }

    if (!newPassword) {
      setNewPasswordError(t('profile.settings.data.change_password.new_password_required'));
      setLoading(false);
      return;
    }

    if (!confirmPassword) {
      setConfirmPasswordError(t('profile.settings.data.change_password.confirm_password_required'));
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(t('profile.settings.data.change_password.confirm_password_not_match'));
      setLoading(false);
      return;
    }

    if (!isCreate && currentPassword === newPassword) {
      setNewPasswordError(t('profile.settings.data.change_password.new_password_same_as_current'));
      setLoading(false);
      return;
    }

    try {
      const url = userChangePasswordUrl();
      const formData = new FormData();
      if (!isCreate) formData.append('user[current_password]', currentPassword || '');
      formData.append('user[new_password]', newPassword || '');

      const fetchOptions = {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      }

      const response = await fetch(url, fetchOptions);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSuccess(true);
      } else {
        const errorMessage = data.error || data.errors?.[0] || t('profile.settings.data.change_password.generic_error');
        if (!isCreate && errorMessage.includes('Current password')) {
          setCurrentPasswordError(errorMessage);
        } else if (errorMessage.includes('New password')) {
          setNewPasswordError(errorMessage);
        } else {
          setNewPasswordError(errorMessage);
        }
      }
    } catch (error) {
      console.error(error);
      setNewPasswordError(t('profile.settings.data.change_password.network_error'));
    } finally {
      setLoading(false);
    }
  }, [currentPassword, newPassword, confirmPassword]);

  const handleSkipPassword = useCallback(() => {
    clearStates();
    onClose();
  }, []);

  if (isSuccess) return <ViewChangePasswordSuccess isCreatePassword={isCreate} />;

  return (
    <div className={viewsStyles.container}>
      <form onSubmit={handleSavePassword} className={viewsStyles.form} id='change-password-form'>
        {!isCreate && (
          <TextInput
            label={t('profile.settings.data.change_password.current_password')}
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword || ''}
            onChange={(value) => handleChangeCurrentPassword(value)}
            error={currentPasswordError}
            adornmentRight={
              <>
                <Button
                  variant='text'
                  icon={showCurrentPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                />
              </>
            }
          />
        )}

        <TextInput
          helper={t('profile.settings.data.change_password.new_password_helper')}
          label={t('profile.settings.data.change_password.new_password')}
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword || ''}
          onChange={(value) => handleChangeNewPassword(value)}
          error={newPasswordError}
          adornmentRight={
            <>
              <Button
                variant='text'
                icon={showNewPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                onClick={() => setShowNewPassword(!showNewPassword)}
              />
            </>
          }
        />

        <TextInput
          label={t('profile.settings.data.change_password.confirm_password')}
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword || ''}
          onChange={(value) => handleChangeConfirmPassword(value)}
          error={confirmPasswordError}
          adornmentRight={
            <>
              <Button
                variant='text'
                icon={showConfirmPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </>
          }
        />

        <div className={viewsStyles.actions}>
          <Button
            variant='secondary'
            classes={{
              root: viewsStyles.actions_button,
            }}
            disabled={loading}
            onClick={handleSkipPassword}
          >
            {t('actions.skip')}
          </Button>

          <Button
            variant='primary'
            type='submit'
            classes={{
              root: viewsStyles.actions_button,
            }}
            disabled={loading}
          >
            {t('actions.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ViewChangePassword;
